import { useEffect, useState } from "react";
import { ApiError, fetchFileBlobUrl } from "@/api/client";
import { useMyDocuments, useSaveMyDates, useSaveMyProfile, useUploadMyDocument, type ProfileInput } from "@/api/queries";
import type { DocType } from "@/api/types";
import { PageHeader } from "@/app/page-header";
import { Button } from "@/components/ui/button";
import { Field, LabelledInput, Textarea } from "@/components/ui/field";
import { Icon } from "@/components/ui/icon";
import { Panel, PanelHeader } from "@/components/ui/panel";
import { ErrorState, SkeletonText } from "@/components/ui/states";
import { toast } from "@/components/ui/toast";
import { formatDate, formatTimestampDate } from "@/lib/format";
import { ALL_DOC_TYPES, DOC_TYPE_LABEL, SELF_UPLOADABLE } from "@/lib/labels";
import { cn } from "@/lib/cn";

export function MyDocumentsPage() {
  const documents = useMyDocuments();
  const save = useSaveMyProfile();
  const saveDates = useSaveMyDates();
  const upload = useUploadMyDocument();

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<ProfileInput>({});
  const [sameAsCurrent, setSameAsCurrent] = useState(false);
  const [busyType, setBusyType] = useState<DocType | null>(null);
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [dateJoined, setDateJoined] = useState("");

  const profile = documents.data?.profile;

  useEffect(() => {
    if (!profile) return;
    setSameAsCurrent(Boolean(profile.same_as_current));
    setForm({
      accountNumber: profile.account_number ?? "",
      accountHolderName: profile.account_holder_name ?? "",
      bankName: profile.bank_name ?? "",
      ifscCode: profile.ifsc_code ?? "",
      currentAddress: profile.current_address ?? "",
      permanentAddress: profile.permanent_address ?? "",
      bloodGroup: profile.blood_group ?? "",
      aadhaarNumber: profile.aadhaar_number ?? "",
      panNumber: profile.pan_number ?? "",
      emergencyContact1Name: profile.emergency_contact_1_name ?? "",
      emergencyContact1Number: profile.emergency_contact_1_number ?? "",
      emergencyContact2Name: profile.emergency_contact_2_name ?? "",
      emergencyContact2Number: profile.emergency_contact_2_number ?? "",
    });
  }, [profile]);

  useEffect(() => {
    setDateOfBirth(documents.data?.dateOfBirth ?? "");
    setDateJoined(documents.data?.dateJoined ?? "");
  }, [documents.data?.dateOfBirth, documents.data?.dateJoined]);

  const set = (key: keyof ProfileInput) => (value: string) =>
    setForm((current) => ({ ...current, [key]: value }));

  async function onSave() {
    try {
      // Gender is deliberately not sent here: this endpoint replaces every
      // profile field, and Account settings owns that single value.
      await save.mutateAsync({
        ...form,
        sameAsCurrent,
        ...(profile?.gender ? { gender: profile.gender } : {}),
      });
      if (dateOfBirth && dateJoined) {
        await saveDates.mutateAsync({ dateOfBirth, dateJoined });
      }
      toast.success("Your details were saved.");
      setEditing(false);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Could not save your details.");
    }
  }

  async function open(docType: DocType) {
    try {
      const url = await fetchFileBlobUrl(`/api/documents/me/file/${docType}`);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Could not open that document.");
    }
  }

  if (documents.error) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader eyebrow="Your account" title="My documents" />
        <Panel>
          <ErrorState message={documents.error.message} onRetry={() => void documents.refetch()} />
        </Panel>
      </div>
    );
  }

  const onFile = new Set((documents.data?.documents ?? []).map((d) => d.doc_type));

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        eyebrow="Your account"
        title="My documents"
        sub="Your own details and documents. Nobody except HR and Admin sees your bank details, address, blood group or emergency contacts."
      />

      <Panel>
        <PanelHeader
          title="Personal details"
          note={
            profile?.updated_at
              ? `Last updated ${formatTimestampDate(profile.updated_at)}`
              : "Entered during your account setup"
          }
          actions={
            editing ? (
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditing(false)}
                  disabled={save.isPending || saveDates.isPending}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  iconLeft="save"
                  onClick={onSave}
                  loading={save.isPending || saveDates.isPending}
                >
                  Save
                </Button>
              </div>
            ) : (
              <Button variant="secondary" size="sm" iconLeft="edit" onClick={() => setEditing(true)}>
                Edit
              </Button>
            )
          }
        />

        <div className="p-4">
          {documents.isLoading ? (
            <SkeletonText lines={8} />
          ) : editing ? (
            <div className="flex flex-col gap-5">
              <fieldset className="grid gap-4 sm:grid-cols-2">
                <legend className="mb-2 text-[13px] font-medium text-ink">Important dates</legend>
                <LabelledInput
                  label="Birthday"
                  type="date"
                  value={dateOfBirth}
                  onChange={(e) => setDateOfBirth(e.target.value)}
                />
                <LabelledInput
                  label="Work anniversary"
                  hint="The date you joined Mentoria."
                  type="date"
                  value={dateJoined}
                  onChange={(e) => setDateJoined(e.target.value)}
                />
              </fieldset>

              <fieldset className="grid gap-4 sm:grid-cols-2">
                <legend className="mb-2 text-[13px] font-medium text-ink">Identity</legend>
                <LabelledInput
                  label="Aadhaar number"
                  value={form.aadhaarNumber ?? ""}
                  onChange={(e) => set("aadhaarNumber")(e.target.value)}
                />
                <LabelledInput
                  label="PAN number"
                  value={form.panNumber ?? ""}
                  onChange={(e) => set("panNumber")(e.target.value)}
                />
                <LabelledInput
                  label="Blood group"
                  hint="Optional."
                  value={form.bloodGroup ?? ""}
                  onChange={(e) => set("bloodGroup")(e.target.value)}
                />
              </fieldset>

              <fieldset className="grid gap-4 sm:grid-cols-2">
                <legend className="mb-2 text-[13px] font-medium text-ink">Bank details</legend>
                <LabelledInput
                  label="Account holder name"
                  value={form.accountHolderName ?? ""}
                  onChange={(e) => set("accountHolderName")(e.target.value)}
                />
                <LabelledInput
                  label="Account number"
                  value={form.accountNumber ?? ""}
                  onChange={(e) => set("accountNumber")(e.target.value)}
                />
                <LabelledInput
                  label="Bank name"
                  value={form.bankName ?? ""}
                  onChange={(e) => set("bankName")(e.target.value)}
                />
                <LabelledInput
                  label="IFSC code"
                  value={form.ifscCode ?? ""}
                  onChange={(e) => set("ifscCode")(e.target.value)}
                />
              </fieldset>

              <fieldset className="flex flex-col gap-4">
                <legend className="mb-2 text-[13px] font-medium text-ink">Address</legend>
                <Field label="Current address" htmlFor="doc-current">
                  <Textarea
                    id="doc-current"
                    value={form.currentAddress ?? ""}
                    onChange={(e) => set("currentAddress")(e.target.value)}
                  />
                </Field>
                <label className="flex items-center gap-2 text-[13px] text-ink-soft">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-line-strong accent-accent"
                    checked={sameAsCurrent}
                    onChange={(e) => setSameAsCurrent(e.target.checked)}
                  />
                  Same as my current address
                </label>
                {!sameAsCurrent && (
                  <Field label="Permanent address" htmlFor="doc-permanent">
                    <Textarea
                      id="doc-permanent"
                      value={form.permanentAddress ?? ""}
                      onChange={(e) => set("permanentAddress")(e.target.value)}
                    />
                  </Field>
                )}
              </fieldset>

              <fieldset className="grid gap-4 sm:grid-cols-2">
                <legend className="mb-2 text-[13px] font-medium text-ink">Emergency contacts</legend>
                <LabelledInput
                  label="Contact 1 name"
                  value={form.emergencyContact1Name ?? ""}
                  onChange={(e) => set("emergencyContact1Name")(e.target.value)}
                />
                <LabelledInput
                  label="Contact 1 number"
                  type="tel"
                  value={form.emergencyContact1Number ?? ""}
                  onChange={(e) => set("emergencyContact1Number")(e.target.value)}
                />
                <LabelledInput
                  label="Contact 2 name"
                  value={form.emergencyContact2Name ?? ""}
                  onChange={(e) => set("emergencyContact2Name")(e.target.value)}
                />
                <LabelledInput
                  label="Contact 2 number"
                  type="tel"
                  value={form.emergencyContact2Number ?? ""}
                  onChange={(e) => set("emergencyContact2Number")(e.target.value)}
                />
              </fieldset>
            </div>
          ) : (
            <dl className="grid gap-x-8 gap-y-0 sm:grid-cols-2">
              {[
                ["Birthday", formatDate(documents.data?.dateOfBirth)],
                ["Work anniversary", formatDate(documents.data?.dateJoined)],
                ["Aadhaar number", profile?.aadhaar_number],
                ["PAN number", profile?.pan_number],
                ["Gender", profile?.gender],
                ["Blood group", profile?.blood_group],
                ["Account holder", profile?.account_holder_name],
                ["Account number", profile?.account_number],
                ["Bank", profile?.bank_name],
                ["IFSC", profile?.ifsc_code],
                ["Current address", profile?.current_address],
                ["Permanent address", profile?.permanent_address],
                [
                  "Emergency contact 1",
                  [profile?.emergency_contact_1_name, profile?.emergency_contact_1_number]
                    .filter(Boolean)
                    .join(" · ") || null,
                ],
                [
                  "Emergency contact 2",
                  [profile?.emergency_contact_2_name, profile?.emergency_contact_2_number]
                    .filter(Boolean)
                    .join(" · ") || null,
                ],
              ].map(([label, value]) => (
                <div
                  key={String(label)}
                  className="flex items-start justify-between gap-4 border-b border-line py-2.5"
                >
                  <dt className="text-[13px] text-muted">{label}</dt>
                  <dd
                    className={cn(
                      "max-w-[60%] text-right text-[13px]",
                      value ? "font-medium text-ink" : "text-faint",
                    )}
                  >
                    {value || "Not provided"}
                  </dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      </Panel>

      <Panel>
        <PanelHeader
          title="Documents"
          note="You can upload your own Aadhaar, PAN and cancelled cheque. Offer letter and compensation sheet are uploaded by HR."
        />
        {documents.isLoading ? (
          <SkeletonText lines={5} className="p-4" />
        ) : (
          <ul className="divide-y divide-line">
            {ALL_DOC_TYPES.map((docType) => {
              const doc = (documents.data?.documents ?? []).find((d) => d.doc_type === docType);
              const canUpload = SELF_UPLOADABLE.includes(docType);
              const isCompensation = docType === "compensation_sheet";
              return (
                <li key={docType} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                  <div className="flex min-w-0 items-center gap-2.5">
                    <Icon
                      name={onFile.has(docType) ? "check-circle" : "document"}
                      size={20}
                      className={onFile.has(docType) ? "text-good" : "text-faint"}
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-ink">{DOC_TYPE_LABEL[docType]}</p>
                      <p className="truncate text-xs text-muted">
                        {doc
                          ? `${doc.original_name} · uploaded ${formatTimestampDate(doc.uploaded_at)}`
                          : canUpload
                            ? "Not uploaded yet"
                            : "Uploaded by HR when available"}
                      </p>
                    </div>
                  </div>

                  <div className="flex shrink-0 items-center gap-2">
                    {doc && (
                      <Button variant="ghost" size="sm" iconLeft="view" onClick={() => void open(docType)}>
                        {isCompensation ? "View" : "View or download"}
                      </Button>
                    )}
                    {canUpload && (
                      <label>
                        <span
                          className={cn(
                            "inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-lg border border-line-strong px-3 text-[13px] font-medium transition-colors hover:bg-surface-sunken active:scale-[0.98]",
                            busyType === docType && "pointer-events-none opacity-50",
                          )}
                        >
                          <Icon
                            name={busyType === docType ? "spinner" : "upload"}
                            size={16}
                            className={busyType === docType ? "animate-spin" : ""}
                          />
                          {doc ? "Replace" : "Upload"}
                        </span>
                        <input
                          type="file"
                          className="sr-only"
                          accept="image/*,application/pdf"
                          onChange={(event) => {
                            const file = event.target.files?.[0];
                            event.target.value = "";
                            if (!file) return;
                            setBusyType(docType);
                            upload.mutate(
                              { docType, file },
                              {
                                onSuccess: () => toast.success(`${DOC_TYPE_LABEL[docType]} uploaded.`),
                                onError: (err) =>
                                  toast.error(
                                    err instanceof ApiError ? err.message : "Upload failed.",
                                  ),
                                onSettled: () => setBusyType(null),
                              },
                            );
                          }}
                        />
                      </label>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Panel>
    </div>
  );
}
